import { call, put, takeEvery, takeLatest, all } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { nctypeApi, NCType, NCTypeSearchParams } from '../../services/nctypeApi';
import {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setValidationErrors,
  setPagination,
  setStatistics,
  setBulkOperationLoading,
  setSuccessMessage,
  clearSuccessMessage,
  FETCH_NCTYPES,
  FETCH_NCTYPE_BY_ID,
  CREATE_NCTYPE,
  UPDATE_NCTYPE,
  DELETE_NCTYPE,
  SEARCH_NCTYPES,
  FETCH_STATISTICS,
  BULK_ACTIVATE,
  BULK_DEACTIVATE,
  BULK_DELETE,
  EXPORT_TO_CSV,
} from '../reducers/nctypeSlice';

// Worker Sagas
function* fetchNCTypesSaga(action: PayloadAction<NCTypeSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const params = action.payload || {};
    const response = yield call(nctypeApi.getAll, params);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: params.page || 1,
      pageSize: params.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error fetching NC Types:', error);
    yield put(setError(error.message || 'Failed to fetch NC Types'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchNCTypeByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const nctype = yield call(nctypeApi.getById, action.payload);
    yield put(setSelectedItem(nctype));
  } catch (error: any) {
    console.error('Error fetching NC Type:', error);
    yield put(setError(error.message || 'Failed to fetch NC Type'));
  } finally {
    yield put(setLoading(false));
  }
}

function* createNCTypeSaga(action: PayloadAction<Omit<NCType, 'Id'>>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(nctypeApi.validation.validateNCType, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const newNCType = yield call(nctypeApi.create, action.payload);
    yield put(addItem(newNCType));
    yield put(setSuccessMessage('NC Type created successfully'));
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      // Note: In a real saga, you'd use a different approach for this
      // This is simplified for demonstration
    }, 3000);
  } catch (error: any) {
    console.error('Error creating NC Type:', error);
    yield put(setError(error.message || 'Failed to create NC Type'));
  } finally {
    yield put(setLoading(false));
  }
}

function* updateNCTypeSaga(action: PayloadAction<{ id: number; nctype: Partial<NCType> }>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const { id, nctype } = action.payload;
    
    // Validate the request
    const validationErrors = yield call(nctypeApi.validation.validateNCType, nctype);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const updatedNCType = yield call(nctypeApi.update, id, nctype);
    yield put(updateItem(updatedNCType));
    yield put(setSuccessMessage('NC Type updated successfully'));
  } catch (error: any) {
    console.error('Error updating NC Type:', error);
    yield put(setError(error.message || 'Failed to update NC Type'));
  } finally {
    yield put(setLoading(false));
  }
}

function* deleteNCTypeSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(nctypeApi.delete, action.payload);
    if (response.success) {
      yield put(removeItem(action.payload));
      yield put(setSuccessMessage('NC Type deleted successfully'));
    } else {
      yield put(setError(response.message || 'Failed to delete NC Type'));
    }
  } catch (error: any) {
    console.error('Error deleting NC Type:', error);
    yield put(setError(error.message || 'Failed to delete NC Type'));
  } finally {
    yield put(setLoading(false));
  }
}

function* searchNCTypesSaga(action: PayloadAction<NCTypeSearchParams>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(nctypeApi.search, action.payload);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: action.payload.page || 1,
      pageSize: action.payload.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error searching NC Types:', error);
    yield put(setError(error.message || 'Failed to search NC Types'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchStatisticsSaga(): Generator<any, void, any> {
  try {
    const statistics = yield call(nctypeApi.getStatistics);
    yield put(setStatistics(statistics));
  } catch (error: any) {
    console.error('Error fetching statistics:', error);
    // Don't set error for statistics as it's not critical
  }
}

function* bulkActivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(nctypeApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors.join(', ')));
      return;
    }
    
    const response = yield call(nctypeApi.bulkActivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(`Successfully activated ${action.payload.length} NC Types`));
      // Refresh the list to show updated status
      yield call(fetchNCTypesSaga, { payload: undefined, type: FETCH_NCTYPES });
    } else {
      yield put(setError(response.message || 'Failed to activate NC Types'));
    }
  } catch (error: any) {
    console.error('Error bulk activating NC Types:', error);
    yield put(setError(error.message || 'Failed to activate NC Types'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

function* bulkDeactivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(nctypeApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors.join(', ')));
      return;
    }
    
    const response = yield call(nctypeApi.bulkDeactivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(`Successfully deactivated ${action.payload.length} NC Types`));
      // Refresh the list to show updated status
      yield call(fetchNCTypesSaga, { payload: undefined, type: FETCH_NCTYPES });
    } else {
      yield put(setError(response.message || 'Failed to deactivate NC Types'));
    }
  } catch (error: any) {
    console.error('Error bulk deactivating NC Types:', error);
    yield put(setError(error.message || 'Failed to deactivate NC Types'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

function* bulkDeleteSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(nctypeApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors.join(', ')));
      return;
    }
    
    const response = yield call(nctypeApi.bulkDelete, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(`Successfully deleted ${action.payload.length} NC Types`));
      // Refresh the list to show updated items
      yield call(fetchNCTypesSaga, { payload: undefined, type: FETCH_NCTYPES });
    } else {
      yield put(setError(response.message || 'Failed to delete NC Types'));
    }
  } catch (error: any) {
    console.error('Error bulk deleting NC Types:', error);
    yield put(setError(error.message || 'Failed to delete NC Types'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

function* exportToCSVSaga(action: PayloadAction<NCTypeSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const params = action.payload || {};
    const blob = yield call(nctypeApi.exportToCSV, params);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `nctypes-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    yield put(setSuccessMessage('NC Types exported successfully'));
  } catch (error: any) {
    console.error('Error exporting NC Types:', error);
    yield put(setError(error.message || 'Failed to export NC Types'));
  } finally {
    yield put(setLoading(false));
  }
}

// Watcher Sagas
function* watchFetchNCTypes(): Generator<any, void, any> {
  yield takeLatest(FETCH_NCTYPES, fetchNCTypesSaga);
}

function* watchFetchNCTypeById(): Generator<any, void, any> {
  yield takeEvery(FETCH_NCTYPE_BY_ID, fetchNCTypeByIdSaga);
}

function* watchCreateNCType(): Generator<any, void, any> {
  yield takeEvery(CREATE_NCTYPE, createNCTypeSaga);
}

function* watchUpdateNCType(): Generator<any, void, any> {
  yield takeEvery(UPDATE_NCTYPE, updateNCTypeSaga);
}

function* watchDeleteNCType(): Generator<any, void, any> {
  yield takeEvery(DELETE_NCTYPE, deleteNCTypeSaga);
}

function* watchSearchNCTypes(): Generator<any, void, any> {
  yield takeLatest(SEARCH_NCTYPES, searchNCTypesSaga);
}

function* watchFetchStatistics(): Generator<any, void, any> {
  yield takeLatest(FETCH_STATISTICS, fetchStatisticsSaga);
}

function* watchBulkActivate(): Generator<any, void, any> {
  yield takeEvery(BULK_ACTIVATE, bulkActivateSaga);
}

function* watchBulkDeactivate(): Generator<any, void, any> {
  yield takeEvery(BULK_DEACTIVATE, bulkDeactivateSaga);
}

function* watchBulkDelete(): Generator<any, void, any> {
  yield takeEvery(BULK_DELETE, bulkDeleteSaga);
}

function* watchExportToCSV(): Generator<any, void, any> {
  yield takeEvery(EXPORT_TO_CSV, exportToCSVSaga);
}

// Root saga
export function* nctypeSaga(): Generator<any, void, any> {
  yield all([
    watchFetchNCTypes(),
    watchFetchNCTypeById(),
    watchCreateNCType(),
    watchUpdateNCType(),
    watchDeleteNCType(),
    watchSearchNCTypes(),
    watchFetchStatistics(),
    watchBulkActivate(),
    watchBulkDeactivate(),
    watchBulkDelete(),
    watchExportToCSV(),
  ]);
} 